import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, isRef as _isRef, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelRadio as _vModelRadio, withDirectives as _withDirectives, withCtx as _withCtx, createBlock as _createBlock } from "vue";
const _hoisted_1 = ["id"];
const _hoisted_2 = { class: "flex flex-col gap-4" };
const _hoisted_3 = { class: "flex flex-col gap-2" };
const _hoisted_4 = { class: "text-sm font-medium text-(--colors-text-heading)" };
const _hoisted_5 = { class: "flex flex-wrap gap-2" };
const _hoisted_6 = ["data-testid"];
const _hoisted_7 = ["value"];
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Badge, Button, DatePickerInput, Modal, TextInput } from "@eudonet/opal";
import { useAppToast } from "@/shared/composables/useAppToast";
import { resolveApiErrorKey } from "@/infrastructure/services/ApiErrorService";
import { useDateIsoBinding } from "@/shared/composables/forms/useDateIsoBinding";
import { toDatePickerFormat } from "@/shared/utils/date-formatting";
import { useProfileStore } from "@/modules/settings/public";
import { MILESTONE_STATUSES, } from "../../../domain/entities/Project";
import { createMilestone, updateMilestone } from "../../../infrastructure/api/projectsApi";
import { milestoneStatusTheme } from "../../composables/milestoneStatusBadge";
import { toWireDate } from "../../composables/wireDate";
const overlayClass = "!z-[300]";
export default /*@__PURE__*/ _defineComponent({
    __name: 'MilestoneFormModal',
    props: /*@__PURE__*/ _mergeModels({
        projectId: {},
        milestone: {}
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["saved"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        const isOpen = _useModel(__props, "modelValue");
        const props = __props;
        const emit = __emit;
        const { t, locale } = useI18n();
        const { toastError } = useAppToast();
        const profileStore = useProfileStore();
        const titleId = useId();
        // Elevated tier: this form opens from ProjectMilestonesWidget's expanded table — the stacking
        // CONSTRAINT lives beside that widget's delete confirmation.
        const submitting = ref(false);
        const label = ref("");
        const targetDateIso = ref("");
        const status = ref("Upcoming");
        const isEdit = computed(() => props.milestone !== null);
        const dateFormat = computed(() => toDatePickerFormat(profileStore.profile?.dateFormat, locale.value));
        const targetDateDisplay = useDateIsoBinding(targetDateIso, computed(() => profileStore.profile?.dateFormat), locale);
        // Re-seed on every open so an edit shows the row's values and a re-opened add starts blank
        // (immediate covers a modal mounted already open).
        watch(isOpen, (open) => {
            if (!open)
                return;
            label.value = props.milestone?.label ?? "";
            targetDateIso.value = props.milestone?.targetDate?.slice(0, 10) ?? "";
            status.value = props.milestone?.status ?? "Upcoming";
        }, { immediate: true });
        const MILESTONE_ERROR_KEYS = {
            "Milestone.Label.Required": "projects.detail.milestones.errors.labelRequired",
            "Milestone.Label.TooLong": "projects.detail.milestones.errors.labelTooLong",
            "Milestone.Status.Required": "projects.detail.milestones.errors.statusInvalid",
            "Milestone.Status.Invalid": "projects.detail.milestones.errors.statusInvalid",
        };
        function handleClose() {
            if (submitting.value)
                return;
            isOpen.value = false;
        }
        async function handleSubmit() {
            if (!label.value.trim() || submitting.value)
                return;
            submitting.value = true;
            try {
                const payload = {
                    label: label.value.trim(),
                    targetDate: toWireDate(targetDateIso.value),
                    status: status.value,
                };
                const saved = props.milestone
                    ? await updateMilestone(props.projectId, props.milestone.milestoneId, payload)
                    : await createMilestone(props.projectId, payload);
                emit("saved", saved);
                isOpen.value = false;
            }
            catch (err) {
                toastError(resolveApiErrorKey(err, MILESTONE_ERROR_KEYS, "projects.detail.milestones.errorMessage"));
            }
            finally {
                submitting.value = false;
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: isOpen.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((isOpen).value = $event)),
                size: "md",
                dismissable: !submitting.value,
                "close-on-escape": !submitting.value,
                "show-close-button": !submitting.value,
                "aria-labelledby": _unref(titleId),
                "overlay-class": overlayClass,
                onClose: handleClose
            }, {
                header: _withCtx(() => [
                    _createElementVNode("h3", {
                        id: _unref(titleId),
                        class: "text-lg font-bold text-(--colors-text-heading)"
                    }, _toDisplayString(isEdit.value ? _unref(t)("projects.detail.milestones.modal.editTitle") : _unref(t)("projects.detail.milestones.modal.addTitle")), 9 /* TEXT, PROPS */, _hoisted_1)
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        label: _unref(t)('common.cancel'),
                        disabled: submitting.value,
                        "data-testid": "milestone-cancel",
                        onClick: handleClose
                    }, null, 8 /* PROPS */, ["label", "disabled"]),
                    _createVNode(_unref(Button), {
                        variant: "primary",
                        label: _unref(t)('projects.detail.milestones.modal.submit'),
                        disabled: !label.value.trim() || submitting.value,
                        loading: submitting.value,
                        "data-testid": "milestone-submit",
                        onClick: handleSubmit
                    }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_2, [
                        _createVNode(_unref(TextInput), {
                            modelValue: label.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((label).value = $event)),
                            label: _unref(t)('projects.detail.milestones.modal.labelLabel'),
                            maxlength: 200,
                            "full-width": "",
                            "data-testid": "milestone-label"
                        }, null, 8 /* PROPS */, ["modelValue", "label"]),
                        _createVNode(_unref(DatePickerInput), {
                            modelValue: _unref(targetDateDisplay),
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => (_isRef(targetDateDisplay) ? (targetDateDisplay).value = $event : null)),
                            label: _unref(t)('projects.detail.milestones.modal.targetDateLabel'),
                            format: dateFormat.value,
                            language: _unref(locale),
                            "week-start": 1,
                            "full-width": "",
                            "data-testid": "milestone-target-date"
                        }, null, 8 /* PROPS */, ["modelValue", "label", "format", "language"]),
                        _createElementVNode("fieldset", _hoisted_3, [
                            _createElementVNode("legend", _hoisted_4, _toDisplayString(_unref(t)("projects.detail.milestones.modal.statusLabel")), 1 /* TEXT */),
                            _createElementVNode("div", _hoisted_5, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(MILESTONE_STATUSES), (option) => {
                                    return (_openBlock(), _createElementBlock("label", {
                                        key: option,
                                        class: "cursor-pointer rounded-full has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-(--colors-text-accent)",
                                        "data-testid": `milestone-status-${option}`
                                    }, [
                                        _withDirectives(_createElementVNode("input", {
                                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((status).value = $event)),
                                            type: "radio",
                                            name: "milestone-status",
                                            value: option,
                                            class: "sr-only"
                                        }, null, 8 /* PROPS */, _hoisted_7), [
                                            [_vModelRadio, status.value]
                                        ]),
                                        _createVNode(_unref(Badge), {
                                            label: _unref(t)(`projects.milestoneStatus.${option}`),
                                            theme: status.value === option ? _unref(milestoneStatusTheme)[option] : 'white',
                                            "show-close-icon": false
                                        }, null, 8 /* PROPS */, ["label", "theme"])
                                    ], 8 /* PROPS */, _hoisted_6));
                                }), 128 /* KEYED_FRAGMENT */))
                            ])
                        ])
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "dismissable", "close-on-escape", "show-close-button", "aria-labelledby"]));
        };
    }
});
